/**
 * The AWS cost panel (§9).
 *
 * Three behaviours here are load-bearing, and all three come from the contract
 * rather than from taste (contracts/admin.ts documents each):
 *
 *  1. **`available: false` hides the card completely.** Not an error, not a
 *     placeholder — the component returns `null`. Cost Explorer may be
 *     unavailable or the Lambda's role may lack `ce:GetCostAndUsage`, and
 *     neither is something an admin looking at user stats needs to hear about.
 *     A cost widget must never be able to break the admin page, which is why
 *     the endpoint answers 200 with a discriminated union instead of failing.
 *  2. **Partial days are ghosted, not hidden.** CE reports with a ~24h lag, so
 *     the most recent day is incomplete. Drawn at full opacity it reads as a
 *     cliff in spend — a fact, rather than an artefact of when we asked.
 *  3. **The cache is surfaced.** CE bills $0.01 per request, so the API caches
 *     for 6 hours and reports `cached` and `fetchedAt`. DragDuro has both on the
 *     wire and shows neither, leaving admins to wonder why a number will not
 *     move; showing them is the one place this deliberately improves on it.
 */

import { Cloud } from 'lucide-react';

import type { CostsResponse } from '@svcdue/contracts';

import { useAdminCosts } from '../api/hooks';
import { Loading } from '../components/states';
import { BarChart } from './BarChart';
import type { BarDatum } from './BarChart';

export function AdminCosts({ days }: { days: number }) {
  const query = useAdminCosts(days, true);

  if (query.isPending) return <Loading label="Loading costs…" />;

  /**
   * A *failed* costs request hides the card too.
   *
   * The endpoint is built never to fail — an outage becomes `available: false`
   * — so if it errored anyway, something is wrong with the deployment, not with
   * the spend. Same conclusion either way: this card has nothing true to say,
   * so it says nothing.
   */
  if (query.isError || !query.data) return null;

  return <CostsView data={query.data} />;
}

export function CostsView({ data }: { data: CostsResponse }) {
  // The discriminant. Everything below is the `available: true` branch, and TS
  // knows it — which is the point of the union.
  if (!data.available) return null;

  const bars: BarDatum[] = data.series.map((point) => ({
    date: point.date,
    value: point.cost,
    partial: point.partial,
    label: `${point.date}: ${money(point.cost, data.currency)}${point.partial ? ' (partial)' : ''}`,
  }));

  const dailyAverage = data.series.length > 0 ? data.total / data.series.length : 0;

  return (
    <div className="card admin-card">
      <div className="admin-h">
        <div>
          <h2 className="h2">
            <Cloud size={14} /> AWS cost
          </h2>
          <span className="admin-sub">via Cost Explorer · ~24h lag (recent days partial)</span>
        </div>
        <span className="cost-fresh" title={new Date(data.fetchedAt).toLocaleString()}>
          {data.cached ? 'cached' : 'fresh'} · {relativeTime(data.fetchedAt)}
        </span>
      </div>

      <div className="stat-grid stat-grid-2">
        <div className="stat-card">
          <div className="stat-label">Total spend</div>
          <div className="stat-n stat-green">{money(data.total, data.currency)}</div>
          <div className="stat-hint">{data.series.length} days</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Average / day</div>
          <div className="stat-n">{money(dailyAverage, data.currency)}</div>
        </div>
      </div>

      <BarChart data={bars} format={(v) => money(v, data.currency)} />

      {data.byService.length > 0 && (
        <div className="cost-services">
          <div className="cost-services-h">By service</div>
          {/* Sorted descending: the top line is where the money goes, and that
              is the only question this table is ever asked. */}
          {[...data.byService]
            .sort((a, b) => b.amount - a.amount)
            .map((s) => (
              <div className="cost-service" key={s.service}>
                <span>{s.service}</span>
                <b>{money(s.amount, data.currency)}</b>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * `Intl.NumberFormat` rather than a `$` and `toFixed(2)`: CE reports whatever
 * currency the account bills in, and hard-coding a dollar sign would silently
 * mislabel a euro account's spend.
 */
function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    // An unrecognised currency code must not take the panel down with it.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
